import React, { useEffect, useState, useMemo } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Plus, FileText, Download, AwardIcon, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../../../components/ui/dialog";
import { PipeSelector } from "../../../../../components/ui/PipeSelector";
import { Label } from "../../../../../components/ui/label";
import { Input } from "../../../../../components/ui/input";
import type { DashboardProject } from '../../../types/project';
import type { SpecificationDocument } from '../../../types/index';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { AddSpecification, getPreSignedUrlToUpload } from '../../../../../redux/Project/specificationSaga';
import customNotification from '../../../../../components/ui/custom-notification';
import { useSelector } from 'react-redux';
import { SPEC_TYPE_LABELS, SPEC_TAG_TYPES } from '../../../constants/specificationConstants';
import { nextAvailableWeldingPassName, WELDING_PASS_NAME_OPTIONS } from '../../../constants/weldingPassNames';
import { Table, Input as AntdInput, Button as AntdButton, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { convertFitmentReadingsObject, convertFitmentReadingsObjectToInches, getUnitFromSettings, getUnitLabel } from '../../../../../utils/unitUtils';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { getListMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { AddMaterialCategory } from '../../../../../redux/Project/materialSaga';


interface WeldProcessDetails {
  processType: string[];
  wireType: string;
  wireSize: string;
  shieldingGas: string;
  flowRate: string;
  voltage: string;
  amperage: string;
  weldPosition: string[];
  jointType: string[];
}

interface SpecificationDetails {
  weldProcess?: WeldProcessDetails;
  inspectionProcedure?: {
    method: string;
    equipment: string;
    frequency: string;
    coverage: string;
    calibrationRequirements: string;
    standardName: string;
  };
  acceptanceCriteria?: {
    standard: string;
    defectTypes: string[];
    tolerances: string;
    repairProcedure: string;
  };
  passConfig?: {
    numberOfPasses: number;
    passes: Array<{
      name: string;
      sequence: number;
      requirements: string;
    }>;
  };
  fitmentReadings?: {
    gapTolerance: string;
    hiLowTolerance: string;
    ovality: string;
    straightness: string;
    measurementPoints: number;
  };
  weldingPosition?: {
    position: string;
    rotationAllowed: boolean;
    maxRotationAngle: string;
    fixedPosition: string;
    restrictions: string[];
  };
  bevel?: {
    type: string;
    angle: string;
    rootFace: string;
    rootGap: string;
    landThickness: string;
    preparation: string;
  };
  /** Spools fabrication WPS — preheat range (°C) for Preheat workflow step. */
  preheatTemperature?: {
    minC: number | null;
    maxC: number | null;
  };
}

interface AddNewSpecificationProps {
  project: DashboardProject;
  onAdd: (spec: SpecificationDocument) => void;
  onClose: () => void;
  isOpen: boolean;
  setupData: any;
}

export const AddNewSpecification: React.FC<AddNewSpecificationProps> = ({ project ,onAdd, onClose, isOpen, setupData }) => {
  const [newSpecification, setNewSpecification] = useState<{
    type: SpecificationDocument['type'] | '';
    pipeId: string;
    fabricationSubType?: string;
    projectType?: string;
    details?: any;
    projectId?: number;
    projectTypeId?: number;
    s3Key?: string;
    uploadedBy?: string;
  }>({
    type: '',
    pipeId: '',
    fabricationSubType: '',
    details: {

      alignment: {
        numberOfAxialAlignments: 2,
        degrees: [0, 90],
        alignmentLocation: [],
        note: 'Internal Line Up Clamp was used for all fit ups. Beveled ends were verified to be square.'
      }
    }
  });

  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [unit, setUnit] = useState<string>(project.project?.unit || 'in');

  const DEFAULT_FLANGE_READING_LABELS = [
    "12 o'clock to 6 o'clock",
    "3 o'clock to 9 o'clock",
  ];

  const buildDefaultFlangeReadingLabels = (count: number): string[] =>
    Array.from({ length: count }, () => '');

  // Document tags state
  const [localDocumentTags, setLocalDocumentTags] = useState<any[]>([]);
  const [selectedTags, setSelectedTags] = useState<{ [fileIndex: number]: string[] }>({});
  const [isAddingTag, setIsAddingTag] = useState(false);

  // Add new item dialog states
  const [isAddingProcessType, setIsAddingProcessType] = useState(false);
  const [isAddingWeldPosition, setIsAddingWeldPosition] = useState(false);
  const [isAddingJointType, setIsAddingJointType] = useState(false);
  const [isAddingAlignmentLocation, setIsAddingAlignmentLocation] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [numberOfAxialAlignments, setNumberOfAxialAlignments] = useState(2);



  const [alignmentLocationOptions, setAlignmentLocationOptions] = useState([
    { value: 'Left End', label: 'Left End' },
    { value: 'Mid 1', label: 'Mid 1' },
    { value: 'Center', label: 'Center' },
    { value: 'Mid 2', label: 'Mid 2' },
    { value: 'Right End', label: 'Right End' }
  ]);
  const [noteOptions, setNoteOptions] = useState('Internal Line Up Clamp was used for all fit ups. Beveled ends were verified to be square.');

  // Default options for the multi-select fields
  const [processTypeOptions, setProcessTypeOptions] = useState<any[]>([]);
  const [weldPositionOptions, setWeldPositionOptions] = useState<any[]>([]);
  const [jointTypeOptions, setJointTypeOptions] = useState<any[]>([]);
  const passOptions = WELDING_PASS_NAME_OPTIONS;

  const [passes, setPasses] = useState<Array<{
    name: string;
    numberOfPasses: number;
    requirements?: string;
  }>>([
    {
      name: 'Hot',
      numberOfPasses: 1,
      requirements: 'Complete penetration, no undercut'
    },
  ]);

  // Tag management functions
  const getSpecificationTags = (specType: string) => {
    const tagType = SPEC_TAG_TYPES[specType];
    if (!tagType) return [];

    return localDocumentTags.filter((tag: any) => tag.tags_type === tagType);
  };

  // Convert specification tags to options for the MultiSelect component
  const tagOptions = useMemo(() => {
    if (!newSpecification.type) return [];

    return getSpecificationTags(newSpecification.type).map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    }));
  }, [localDocumentTags, newSpecification.type]);

  const user = JSON.parse(localStorage.getItem('user') || '{}')

  useEffect(() => {
    const loadInitialData = async () => {
      try {

        // Load departments and companies in parallel


        // const [weldProcessList, weldPositionList, jointTypeList, alignmentLocationList, documentTagsList] = await Promise.all([
        //   getListMaterialCategory('WELD_PROCESS_SPECS'),
        //   getListMaterialCategory('WELD_POSITION_SPECS'),
        //   getListMaterialCategory('JOINT_TYPE_SPECS'),
        //   getListMaterialCategory('ALIGNMENT_LOCATION_SPECS'),
        //   getListMaterialCategory('SPECIFICATION_DOCUMENT_TAGS'),
        // ]);
        const alltags = await getListMaterialCategory()

        const weldProcessList = alltags.filter((tag: any) => tag.tags_type === 'WELD_PROCESS_SPECS');
        const weldPositionList = alltags.filter((tag: any) => tag.tags_type === 'WELD_POSITION_SPECS');
        const jointTypeList = alltags.filter((tag: any) => tag.tags_type === 'JOINT_TYPE_SPECS');
        const alignmentLocationList = alltags.filter((tag: any) => tag.tags_type === 'ALIGNMENT_LOCATION_SPECS');


        // Check if any of the responses are null or empty
        if (!weldProcessList || !weldPositionList || !jointTypeList) {
          //throw new Error('API calls returned null or empty data');
        }

        // Remove duplicates and sort companies alphabetically by company_name
        const uniqueWeldProcessList = weldProcessList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setProcessTypeOptions(uniqueWeldProcessList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));

        const uniqueWeldPositionList = weldPositionList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setWeldPositionOptions(uniqueWeldPositionList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));

        const uniqueJointTypeList = jointTypeList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));

        setJointTypeOptions(uniqueJointTypeList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));


        const uniqueAlignmentLocationList = alignmentLocationList
          .filter((tag: any, index: number, self: any[]) =>
            index === self.findIndex((t: any) => t.tags_name === tag.tags_name)
          )
          .sort((a: any, b: any) => a.tags_name.localeCompare(b.tags_name));
        if (uniqueAlignmentLocationList.length > 0) {
          setAlignmentLocationOptions(uniqueAlignmentLocationList.map((tag: any) => ({ id: tag.tags_id, value: tag.tags_name, label: tag.tags_name })));
        }

        // Set document tags
        if (alltags && alltags.length > 0) {
          setLocalDocumentTags(alltags);
        }


      } catch (error: any) {

        // // Show user-friendly error message
        // if (error.response?.status === 401) {
        //   customNotification.error('Authentication Error', 'Your session has expired. Please log in again.');
        // } else if (error.response?.status === 403) {
        //   customNotification.error('Permission Denied', 'You do not have permission to access this resource.');
        // } else if (error.response?.status >= 500) {
        //   customNotification.error('Server Error', 'The server is experiencing issues. Please try again later.');
        // } else if (error.code === 'NETWORK_ERROR') {
        //   customNotification.error('Network Error', 'Unable to connect to the server. Please check your internet connection.');
        // } else {
        //   customNotification.error('Error', `Failed to load data: ${error.message}`);
        // }

        // Set fallback options if API calls fail
        // setProcessTypeOptions([
        //   { value: 'GMAW', label: 'GMAW' },
        //   { value: 'FCAW', label: 'FCAW' },
        //   { value: 'SMAW', label: 'SMAW' },
        //   { value: 'GTAW', label: 'GTAW' },
        //   { value: 'SAW', label: 'SAW' }
        // ]);
        // setWeldPositionOptions([
        //   { value: '1G-Flat', label: '1G - Flat' },
        //   { value: '2G-Horizontal', label: '2G - Horizontal' },
        //   { value: '3G-Vertical', label: '3G - Vertical' },
        //   { value: '4G-Overhead', label: '4G - Overhead' },
        //   { value: '5G-Pipe Vertical', label: '5G - Pipe Vertical' },
        //   { value: '6G-Pipe 45°', label: '6G - Pipe 45°' }
        // ]);
        // setJointTypeOptions([
        //   { value: 'Butt Joint', label: 'Butt Joint' },
        //   { value: 'Lap Joint', label: 'Lap Joint' },
        //   { value: 'T-Joint', label: 'T-Joint' },
        //   { value: 'Corner Joint', label: 'Corner Joint' },
        //   { value: 'Edge Joint', label: 'Edge Joint' },
        //   { value: 'J-Prep', label: 'J-Prep' }
        // ]);
      }
    };
    if (processTypeOptions.length === 0 && weldPositionOptions.length === 0 && jointTypeOptions.length === 0) {
      // Always load initial data when component mounts
      loadInitialData();
    }
  }, [setupData]);


  // Handlers for adding new items
  const handleAddProcessType = async (newProcessType: string) => {
    if (newProcessType.trim()) {
      if (processTypeOptions.find((tag: any) => tag.tags_name === newProcessType.trim())) {
        customNotification.warning(`${newProcessType.trim()} tag already exists`);

        return;
      }
      const apiPayload = {
        tags_name: newProcessType.trim(),
        project_id: setupData.projectId,
        tags_type: 'WELD_PROCESS_SPECS'
      }
      const res = await AddMaterialCategory(apiPayload, 'process type');
      if (res) {
        setProcessTypeOptions([...processTypeOptions, { id: res.tags_id, value: res.tags_name, label: res.tags_name }]);
        // Automatically select the newly added tag
        setNewSpecification(prev => ({
          ...prev,
          details: {
            ...prev.details,
            processType: [...(prev.details?.processType || []), newProcessType]
          }
        }));
      }
    }





  };

  const handleAddWeldPosition = async (newWeldPosition: string) => {



    if (newWeldPosition.trim()) {
      if (weldPositionOptions.find((tag: any) => tag.tags_name === newWeldPosition.trim())) {
        customNotification.warning(`${newWeldPosition.trim()} tag already exists`);
        return;
      }
    } const apiPayload = {
      tags_name: newWeldPosition.trim(),
      project_id: setupData.projectId,
      tags_type: 'WELD_POSITION_SPECS'
    }
    const res = await AddMaterialCategory(apiPayload, 'weld position');
    if (res) {
      setWeldPositionOptions([...weldPositionOptions, { id: res.tags_id, value: res.tags_name, label: res.tags_name }]);
      setNewSpecification(prev => ({
        ...prev,
        details: {
          ...prev.details,
          weldPosition: [...(prev.details?.weldPosition || []), newWeldPosition]
        }
      }));
    }
  };

  const handleAddJointType = async (newJointType: string) => {
    if (newJointType.trim()) {
      if (jointTypeOptions.find((tag: any) => tag.tags_name === newJointType.trim())) {
        customNotification.warning(`${newJointType.trim()} tag already exists`);
        return;
      }
    }
    const apiPayload = {
      tags_name: newJointType.trim(),
      project_id: setupData.projectId,
      tags_type: 'JOINT_TYPE_SPECS'
    }
    const res = await AddMaterialCategory(apiPayload, 'joint type');
    if (res) {
      setJointTypeOptions([...jointTypeOptions, { id: res.tags_id, value: res.tags_name, label: res.tags_name }]);
      setNewSpecification(prev => ({
        ...prev,
        details: {
          ...prev.details,
          jointType: [...(prev.details?.jointType || []), newJointType]
        }
      }));
    }
  };

  const handleAddAlignmentLocation = async (newAlignmentLocation: string) => {
    if (newAlignmentLocation.trim()) {
      // Check if alignment location already exists
      if (alignmentLocationOptions.find(option => option.value === newAlignmentLocation.trim())) {
        customNotification.warning(`${newAlignmentLocation.trim()} already exists`);
        return;
      }

      // const newOption = { value: newAlignmentLocation, label: newAlignmentLocation };


      const apiPayload = {
        tags_name: newAlignmentLocation.trim(),
        project_id: setupData.projectId,
        tags_type: 'ALIGNMENT_LOCATION_SPECS'
      }
      const res = await AddMaterialCategory(apiPayload, 'alignment location');
      if (res) {
        setAlignmentLocationOptions([...alignmentLocationOptions, { value: res.tags_name, label: res.tags_name }]);

        const currentLocations = newSpecification.details?.alignment?.alignmentLocation || [];
        const updatedLocations = [...currentLocations, res.tags_name];
        updateAlignmentField('alignmentLocation', updatedLocations);
      }



      // Show success message
      // customNotification.success('Alignment Location Added', `${newAlignmentLocation.trim()} has been added successfully.`);
    }
  };

  const handleAddTag = async (newTagName: string) => {
    if (newTagName.trim()) {
      const specType = newSpecification.type;
      if (!specType) {
        customNotification.warning('Please select a specification type first');
        return;
      }

      const tagType = SPEC_TAG_TYPES[specType];
      if (!tagType) {
        customNotification.warning('Invalid specification type for tags');
        return;
      }

      // Check if tag already exists
      if (getSpecificationTags(specType).find((tag: any) => tag.tags_name === newTagName.trim())) {
        customNotification.warning(`${newTagName.trim()} tag already exists`);
        return;
      }

      const apiPayload = {
        tags_name: newTagName.trim(),
        project_id: setupData.projectId,
        tags_type: tagType
      };

      const res = await AddMaterialCategory(apiPayload, 'specification tag');
      if (res) {
        // Update local tags
        setLocalDocumentTags(prev => [...prev, res]);

        // Automatically select the newly added tag for the first file if it exists
        if (files.length > 0) {
          const newTagValue = res.tags_id.toString();
          setSelectedTags(prev => ({
            ...prev,
            [0]: [...(prev[0] || []), newTagValue]
          }));
        }

        //customNotification.success('Tag Added', `${newTagName.trim()} has been added successfully.`);
      }
    }
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    setSelectedTags(prev => ({
      ...prev,
      [fileIndex]: tags
    }));
  };

  // const handleAddNote = (newNote: string) => {
  //   if (newNote.trim()) {
  //     // Check if note already exists
  //     if (noteOptions.find(option => option.label === newNote.trim())) {
  //       customNotification.warning(`${newNote.trim()} already exists`);
  //       return;
  //     }

  //     const newOption = { value: newNote.trim(), label: newNote.trim() };
  //     setNoteOptions(prev => [...prev, newOption]);
  //     // Also add to the current selection
  //     setNewSpecification(prev => ({
  //       ...prev,
  //       details: {
  //         ...prev.details,
  //         alignment: {
  //           ...prev.details?.alignment,
  //           note: [...(prev.details?.alignment?.note || []), newNote.trim()]
  //         }
  //       }
  //     }));

  //     // Show success message
  //     customNotification.success('Note Added', `Note has been added successfully.`);
  //   }
  // };

  useEffect(() => {

    if (project.project.unit) {
      setUnit(project.project.unit || 'in');
    }
  }, [project.project.unit]);

  // Extract pipe data from setupData
  const pipeOptions = useMemo(() => {
    const pipes: Array<{
      pipeId: string;
      displayName: string;
      size: number;
      wallThickness: number;
      subType: string;
      isFlangePipe?: boolean;
    }> = [];

    if (setupData && typeof setupData === 'object') {
      // Handle different setup data structures
      Object.entries(setupData).forEach(([key, setup]: [string, any]) => {
        if (setup && setup.pipeSizes && Array.isArray(setup.pipeSizes)) {
          setup.pipeSizes.forEach((pipe: any) => {
            if (pipe) {
              pipes.push({
                pipeId: pipe.pipeId,
                displayName: `Pipe ${pipe.pipeOrder} (${pipe.pipeSize || 0}" × ${pipe.wallThickness || 0}")`,
                size: pipe.pipeSize || 0,
                wallThickness: pipe.wallThickness || 0,
                subType: setup.projectType === 'pipeline' ? 'pipeline' : setup.projectType === 'others' ? 'others' : (setup.projectSubType || ''),
                isFlangePipe: !!pipe.pipeDetailsExtraDetails?.isFlangePipe,
              });
            }
          });
        }
      });
    }

    return pipes;
  }, [setupData]);

  const selectedPipeIsFlange = useMemo(() => {
    if (!newSpecification.pipeId) return false;
    return !!pipeOptions.find((p) => String(p.pipeId) === String(newSpecification.pipeId))?.isFlangePipe;
  }, [pipeOptions, newSpecification.pipeId]);

  // When flange pipe + ALIGNMENT: seed number of readings + default clock labels
  useEffect(() => {
    if (!selectedPipeIsFlange || newSpecification.type !== 'ALIGNMENT') return;
    setNewSpecification((prev) => {
      const alignment = prev.details?.alignment || {};
      const count = Number(alignment.numberOfFlangeReadings) || 2;
      const hasLabels =
        Array.isArray(alignment.flangeReadings) && alignment.flangeReadings.length > 0;
      if (alignment.alignmentMode === 'flange' && hasLabels) {
        return prev;
      }
      return {
        ...prev,
        details: {
          ...prev.details,
          alignment: {
            ...alignment,
            alignmentMode: 'flange',
            numberOfFlangeReadings: count,
            flangeReadings: buildDefaultFlangeReadingLabels(count),
            note:
              alignment.note ||
              'Internal Line Up Clamp was used for all fit ups. Beveled ends were verified to be square.',
          },
        },
      };
    });
  }, [selectedPipeIsFlange, newSpecification.type, newSpecification.pipeId]);

  // Get available fabrication subtypes from setup data
  const fabricationSubTypes = useMemo(() => {
    const subTypes = new Set<string>();

    // Add pipeline as default option


    if (setupData && typeof setupData === 'object') {
      Object.values(setupData).forEach((setup: any) => {
        if (setup.projectType === 'pipeline') {
          subTypes.add('pipeline');
        }
        else if (setup && setup.projectType === 'fabrication' && setup.projectSubType) {
          subTypes.add(setup.projectSubType);
        }
        else if (setup && setup.projectType === 'others') {
          subTypes.add('others');
        }
      });
    }
    return Array.from(subTypes);
  }, [setupData]);

  // Auto-select default option if only one is available
  useEffect(() => {
    if (fabricationSubTypes.length === 1) {
      setNewSpecification(prev => ({
        ...prev,
        fabricationSubType: fabricationSubTypes[0]
      }));
    }
  }, [fabricationSubTypes]);

  // Filter pipe options based on selected fabrication subtype.
  // Applies uniformly to pipeline / others / fabrication subtypes.
  const filteredPipeOptions = useMemo(() => {
    if (!newSpecification.fabricationSubType) {
      return pipeOptions;
    }
    return pipeOptions.filter(
      (pipe: { pipeId: string; displayName: string; size: number; wallThickness: number; subType: string }) =>
        pipe.subType === newSpecification.fabricationSubType
    );
  }, [newSpecification.fabricationSubType, pipeOptions]);

  // Check if current setup data is for fabrication or others
  const isFabricationSetup = () => {
    if (!setupData || typeof setupData !== 'object') return false;
    return Object.values(setupData).some((setup: any) => setup &&
      (setup.projectType === 'fabrication'));
  };
  const isAlreadyContainSpecification = (projectTypeId: any, pipeId: any, type: any) => {
    if (!setupData || typeof setupData !== 'object') {
      return false;
    }

    // Use for...of loops instead of forEach to allow early return
    for (const [key, setup] of Object.entries(setupData)) {
      const setupData = setup as any; // Type assertion to resolve the error
      if (setupData && setupData.pipeSizes && Array.isArray(setupData.pipeSizes)) {
        for (const pipe of setupData.pipeSizes) {
          if (pipe && pipe.pipeId === pipeId) {
            if (pipe.documentsData && pipe.documentsData.documents) {
              const hasSpecification = pipe.documentsData.documents.some((specification: any) =>
                specification && specification.type === type
              );

              if (hasSpecification) {
                return true; // Early return when found
              }
            }
          }
        }
      }
    }

    return false;
  }

  // useEffect(() => {
  // }, [setupData, pipeOptions, fabricationSubTypes, newSpecification.fabricationSubType, filteredPipeOptions]);

  // Reset pipe selection when fabrication subtype changes
  useEffect(() => {
    if (newSpecification.fabricationSubType && newSpecification.pipeId) {
      // Check if current pipe selection is still valid for the new subtype
      const currentPipe = pipeOptions.find(pipe => pipe.pipeId === newSpecification.pipeId);
      if (currentPipe && currentPipe.subType !== newSpecification.fabricationSubType) {
        setNewSpecification(prev => ({ ...prev, pipeId: '' }));
      }
    }
  }, [newSpecification.fabricationSubType, pipeOptions]);

  // Clear tags when specification type changes
  useEffect(() => {
    setSelectedTags({});
  }, [newSpecification.type]);



  const handleSubmit = async () => {


    if (!newSpecification.fabricationSubType) {
      customNotification.warning('Please select a project type');
      return;
    }

    if (!newSpecification.type) {
      customNotification.warning('Please select a specification type');
      return;
    }

    if (!newSpecification.pipeId) {
      customNotification.warning('Please select a pipe');
      return;
    }

    if (newSpecification.type !== 'WELD_PROCESS' && newSpecification.type !== 'PASS_CONFIG' && newSpecification.type !== 'FITMENT_READINGS' && newSpecification.type !== 'ALIGNMENT' && (!files || files.length === 0)) {
      customNotification.warning('Please upload at least one file');
      return;
    }
    if (newSpecification.type === 'PASS_CONFIG' && passes.length > 0) {

      passes.forEach((pass, index) => {
        if (!pass.name) {
          customNotification.warning(`Please add a  name of pass ${index + 1} `);
          if (pass.numberOfPasses === 0) {
            customNotification.warning(`Please add a valid number of passes for pass ${index + 1}`);
            return;
          }

          return;
        }
      });
    }

    if (newSpecification.type === 'FITMENT_READINGS') {
      const fitmentReadingsDetails = newSpecification.details?.fitmentReadings;
     

      const isSpoolsFitment =
        newSpecification.fabricationSubType === 'spools';

      if (isSpoolsFitment) {
        const spoolsMinMaxPairs: { min: unknown; max: unknown; label: string }[] = [
          {
            min: fitmentReadingsDetails?.internalHiLowMin,
            max: fitmentReadingsDetails?.internalHiLowMax,
            label: 'internal hi-low',
          },
          {
            min: fitmentReadingsDetails?.wallThicknessTopRangeMin,
            max: fitmentReadingsDetails?.wallThicknessTopRangeMax,
            label: 'wall thickness top range',
          },
          {
            min: fitmentReadingsDetails?.gapToleranceMin,
            max: fitmentReadingsDetails?.gapToleranceMax,
            label: 'gap tolerance range',
          },
        ];
        for (const { min, max, label } of spoolsMinMaxPairs) {
          const hasMin = min != null && min !== '';
          const hasMax = max != null && max !== '';
          if (hasMin && hasMax && Number(min) >= Number(max)) {
            customNotification.warning(
              `Minimum ${label} must be less than maximum ${label}`
            );
            return;
          }
        }
      } else {

        if (!fitmentReadingsDetails?.numberOfReadings) {
          customNotification.warning('Please add a number of readings');
          return;
        }
        if (!fitmentReadingsDetails?.landMin) {
          customNotification.warning('Please add a land minimum');
          return;
        }
        if (!fitmentReadingsDetails?.landMax) {
          customNotification.warning('Please add a land maximum');
          return;
        }
        if (!fitmentReadingsDetails?.wallThicknessMin) {
          customNotification.warning('Please add a wall thickness minimum');
          return;
        }
        if (!fitmentReadingsDetails?.wallThicknessMax) {
          customNotification.warning('Please add a wall thickness max');
          return;
        }

        if (Number(fitmentReadingsDetails?.wallThicknessMin) >= Number(fitmentReadingsDetails?.wallThicknessMax)) {
          customNotification.warning('Minimum wall thickness cannot be greater than maximum wall thickness');
          return;
        }
        if (Number(fitmentReadingsDetails?.landMin) >= Number(fitmentReadingsDetails?.landMax)) {
          customNotification.warning('Minimum land cannot be greater than maximum land');
          return;
        }

        if (!fitmentReadingsDetails?.internalHiLowMin) {
          customNotification.warning('Please add an internal hi-low minimum');
          return;
        }
        if (!fitmentReadingsDetails?.internalHiLowMax) {
          customNotification.warning('Please add an internal hi-low maximum');
          return;
        }
        if (Number(fitmentReadingsDetails?.internalHiLowMin) >= Number(fitmentReadingsDetails?.internalHiLowMax)) {
          customNotification.warning('Minimum internal hi-low cannot be greater than maximum internal hi-low');
          return;
        }

        if (!fitmentReadingsDetails?.externalHiLowMin) {
          customNotification.warning('Please add an external hi-low minimum');
          return;
        }
        if (!fitmentReadingsDetails?.externalHiLowMax) {
          customNotification.warning('Please add an external hi-low maximum');
          return;
        }
        if (Number(fitmentReadingsDetails?.externalHiLowMin) >= Number(fitmentReadingsDetails?.externalHiLowMax)) {
          customNotification.warning('Minimum external hi-low cannot be greater than maximum external hi-low');
          return;
        }

        if (!fitmentReadingsDetails?.bevelTopRangeMin) {
          customNotification.warning('Please add a bevel top range minimum');
          return;
        }
        if (!fitmentReadingsDetails?.bevelTopRangeMax) {
          customNotification.warning('Please add a bevel top range maximum');
          return;
        }
        if (Number(fitmentReadingsDetails?.bevelTopRangeMin) >= Number(fitmentReadingsDetails?.bevelTopRangeMax)) {
          customNotification.warning('Minimum bevel top range cannot be greater than maximum bevel top range');
          return;
        }
      }
    }

    // Validation for ALIGNMENT type
    if (
      newSpecification.type === 'WPS' &&
      newSpecification.fabricationSubType === 'spools'
    ) {
      const preheat = newSpecification.details?.preheatTemperature;
      if (preheat?.minC == null || preheat?.minC === '') {
        customNotification.warning('Please enter preheat temperature minimum (°C)');
        return;
      }
      if (preheat?.maxC == null || preheat?.maxC === '') {
        customNotification.warning('Please enter preheat temperature maximum (°C)');
        return;
      }
      if (Number(preheat.minC) >= Number(preheat.maxC)) {
        customNotification.warning(
          'Preheat minimum must be less than preheat maximum'
        );
        return;
      }
    }

    if (newSpecification.type === 'ALIGNMENT') {
      const alignmentDetails = newSpecification.details?.alignment;
      const isFlangeAlignment =
        selectedPipeIsFlange || alignmentDetails?.alignmentMode === 'flange';

      if (isFlangeAlignment) {
        const count = Number(alignmentDetails?.numberOfFlangeReadings);
        if (!count || count < 1 || count > 10) {
          customNotification.warning('Number of Readings must be between 1 and 10');
          return;
        }
        const labels = alignmentDetails?.flangeReadings || [];
        if (!Array.isArray(labels) || labels.length < count) {
          customNotification.warning('Please enter a label for each flange reading');
          return;
        }
        if (labels.slice(0, count).some((label: string) => !String(label || '').trim())) {
          customNotification.warning('Please enter a label for each flange reading');
          return;
        }
        if (!alignmentDetails?.alignmentLocation ||
          alignmentDetails.alignmentLocation.length === 0) {
          customNotification.warning('Please select at least one Alignment Location');
          return;
        }
        if (!alignmentDetails?.note || String(alignmentDetails.note).trim().length === 0) {
          customNotification.warning('Please enter a Note for flange alignment');
          return;
        }
      } else {
        // Validate Number of Locations (2-10)
        if (!alignmentDetails?.numberOfAxialAlignments ||
          Number(alignmentDetails.numberOfAxialAlignments) < 2 ||
          Number(alignmentDetails.numberOfAxialAlignments) > 10) {
          customNotification.warning('Number of Locations must be between 2 and 10');
          return;
        }

        // Validate Alignment Location is selected
        if (!alignmentDetails?.alignmentLocation ||
          alignmentDetails.alignmentLocation.length === 0) {
          customNotification.warning('Please select at least one Alignment Location');
          return;
        }

        // Validate Note is selected
        if (!alignmentDetails?.note ||
          alignmentDetails.note.length === 0) {
          customNotification.warning('Please select at least one Note');
          return;
        }
      }
    }


    // if (newSpecification.type === 'FITMENT_READINGS') {
    //   const fitmentReadingsDetails = newSpecification.details?.fitmentReadings;

    //   newSpecification.details = {
    //     ...newSpecification.details,
    //     fitmentReadings: convertFitmentReadingsObjectToInches(fitmentReadingsDetails)
    //   };

    // }





    // Determine projectId and projectTypeId from the selected project type (not isFabricationSetup(),
    // which is true whenever any fabrication entry exists — even when user picked pipeline).
    let projectId, projectTypeId;

    if (newSpecification.fabricationSubType === 'pipeline') {
      const pipelineSetupData = Object.values(setupData).find((setup: any) => setup?.projectType === 'pipeline') as any;
      projectId = pipelineSetupData?.projectId;
      projectTypeId = pipelineSetupData?.projectTypeId;
    } else if (newSpecification.fabricationSubType === 'others') {
      const othersSetupData = Object.values(setupData).find((setup: any) => setup?.projectType === 'others') as any;
      projectId = othersSetupData?.projectId;
      projectTypeId = othersSetupData?.projectTypeId;
    } else {
      const fabricationSetupData = Object.values(setupData).find((setup: any) =>
        setup?.projectType === 'fabrication' && setup?.projectSubType === newSpecification.fabricationSubType
      ) as any;
      projectId = fabricationSetupData?.projectId;
      projectTypeId = fabricationSetupData?.projectTypeId;
    }

    if (!projectId || !projectTypeId) {
      const pipeSetup = Object.values(setupData).find((setup: any) =>
        setup?.pipeSizes?.some((pipe: any) => String(pipe.pipeId) === String(newSpecification.pipeId))
      ) as any;
      projectId = projectId || pipeSetup?.projectId;
      projectTypeId = projectTypeId || pipeSetup?.projectTypeId;
    }
    // if (newSpecification.type === 'ALIGNMENT' || newSpecification.type === 'WELD_PROCESS' || newSpecification.type === 'FITMENT_READINGS' || newSpecification.type === 'PASS_CONFIG') {

    //   const isContain = isAlreadyContainSpecification(projectTypeId, newSpecification.pipeId, newSpecification.type)


    //   if (isContain) {
    //     customNotification.warning(`${SPEC_TYPE_LABELS[newSpecification.type]} already exists`,
    //       `Please edit the existing specification ${filteredPipeOptions.find((pipe: any) => pipe.pipeId === newSpecification.pipeId)?.displayName}`);
    //     return;
    //   }
    // }

    setIsLoading(true);

    let s3Response: any[] = [];

    if (files.length > 0) {

      const payload = {
        pipeId: Number(newSpecification.pipeId),
        projectId: Number(projectId),
        projectTypeId: Number(projectTypeId),
        type: newSpecification.type
      }
      s3Response = await getPreSignedUrlToUpload(files, payload);
    }



    if (newSpecification.type && newSpecification.pipeId) {
      const newDoc: any = {
        // id: Math.random().toString(36).substr(2, 9),
        //  fileName: files.length > 0 ? files[0].name : '',
        type: newSpecification.type as SpecificationDocument['type'],
        pipeId: Number(newSpecification.pipeId),
        details: newSpecification.type === 'PASS_CONFIG'
          ? {
            passConfig: {
              numberOfPasses: passes.length,
              passes: passes.map((pass) => ({
                name: pass.name,
                numberOfPasses: pass.numberOfPasses,
                requirements: pass.requirements
              }))
            }
          }
          : newSpecification.type === 'WELD_PROCESS'
            ? {
              weldProcess: newSpecification.details?.weldProcess
            } : newSpecification.type === 'ALIGNMENT'
              ? {
                alignment: {
                  ...newSpecification.details?.alignment,
                  alignmentMode: selectedPipeIsFlange ? 'flange' : (newSpecification.details?.alignment?.alignmentMode || 'axial'),
                }
              }

              : newSpecification.type === 'FITMENT_READINGS'
                ? {
                  //    fitmentReadings: newSpecification.details?.fitmentReadings
                  fitmentReadings: convertFitmentReadingsObjectToInches(newSpecification.details?.fitmentReadings)
                }

                : newSpecification.type === 'COATING'
                  ? {
                    coating: {
                      checklistUi: 'CoatingChecklistSpecificationModal',
                      note: 'Coating checklist document row; edit checklist from the specification eye button.',
                    },
                  }
                  : newSpecification.type === 'WPS'
                    ? {
                        ...(newSpecification.details ?? {}),
                        ...(newSpecification.fabricationSubType === 'spools' &&
                        newSpecification.details?.preheatTemperature
                          ? {
                              preheatTemperature: newSpecification.details.preheatTemperature,
                            }
                          : {}),
                      }
                    : newSpecification.details,
        projectId: projectId,
        projectTypeId: projectTypeId,

        uploadDate: new Date().toISOString(),
        uploadedBy: user.email || 'no email'
      };

      if (s3Response && s3Response.length > 0) {
        newDoc.files = s3Response.map((file: any, index: number) => ({
          // version: file.version || 1,
          fileName: file.fileName,
          s3Key: file.s3Key,
          tags: selectedTags[index] ? selectedTags[index].map((tag: any) => Number(tag)) : []
        }))
      }

      console.log(newDoc);

      const response = await AddSpecification([newDoc], false)


      if (response) {
        customNotification.success('Specification added successfully', `The ${SPEC_TYPE_LABELS[newSpecification.type]} document has been uploaded.`);




        // Notify parent component immediately with the new specification
        await onAdd(response);

        onReset();
        setIsLoading(false);
        // Close dialog after a brief delay to ensure UI updates
        // setTimeout(() => {

        // }, 200);

        // Show success notification
      } else {
        customNotification.error('Failed to add specification', 'Please try again.');
      }
    }
    setIsLoading(false);
  };
  const onReset = () => {
    setNewSpecification({
      type: '',
      pipeId: '',
      fabricationSubType: '',
      details: {
        processType: [],

        weldPosition: [],
        jointType: [],
        alignment: {
          numberOfAxialAlignments: 2,
          degrees: [0, 90],
          alignmentLocation: [],
          note: []
        }
      }
    });
    setPasses([
      {
        name: 'Hot',
        numberOfPasses: 1,
        requirements: 'Complete penetration, no undercut'
      }
    ]);
    setFiles([]);
    setSelectedTags({});
    setIsLoading(false);

    onClose();
  }

  const updateWeldProcessField = (field: string, value: string | string[]) => {
    setNewSpecification(prev => ({
      ...prev,
      details: {
        ...prev.details,
        weldProcess: {
          ...prev.details?.weldProcess,
          [field]: value
        }
      }
    }));
  };

  const updateInspectionProcedureField = (field: string, value: string) => {
    setNewSpecification(prev => ({
      ...prev,
      details: {
        inspectionProcedure: {
          method: prev.details?.inspectionProcedure?.method || '',
          equipment: prev.details?.inspectionProcedure?.equipment || '',
          frequency: prev.details?.inspectionProcedure?.frequency || '',
          coverage: prev.details?.inspectionProcedure?.coverage || '',
          calibrationRequirements: prev.details?.inspectionProcedure?.calibrationRequirements || '',
          standardName: prev.details?.inspectionProcedure?.standardName || '',
          [field]: value
        }
      }
    }));
  };

  const updateWeldingPositionField = (field: string, value: any) => {
    setNewSpecification(prev => ({
      ...prev,
      details: {
        ...prev.details,
        weldingPosition: {
          position: prev.details?.weldingPosition?.position || '',
          rotationAllowed: prev.details?.weldingPosition?.rotationAllowed || false,
          maxRotationAngle: prev.details?.weldingPosition?.maxRotationAngle || '',
          fixedPosition: prev.details?.weldingPosition?.fixedPosition || '',
          restrictions: prev.details?.weldingPosition?.restrictions || [],
          [field]: value
        }
      }
    }));
  };

  const updateFitmentReadingsField = (field: string, value: any) => {


    setNewSpecification(prev => ({
      ...prev,
      details: {
        ...prev.details,
        fitmentReadings: {
          ...prev.details?.fitmentReadings,
          [field]: value
        }
      }
    }));
  };

  const updatePreheatTemperatureField = (field: 'minC' | 'maxC', value: number | null) => {
    setNewSpecification((prev) => ({
      ...prev,
      details: {
        ...prev.details,
        preheatTemperature: {
          ...prev.details?.preheatTemperature,
          [field]: value,
        },
      },
    }));
  };

  const updateAlignmentField = (field: string, value: any) => {

    setNewSpecification(prev => {
      const updatedAlignment = {
        ...prev.details?.alignment,
        [field]: value
      };

      // If updating numberOfAxialAlignments, auto-populate default degrees
      if (field === 'numberOfAxialAlignments') {
        if (value > 0) {
          const defaultDegrees = Array.from({ length: value }, (_, index) =>
            ((index * 90) % 360)
          );
          updatedAlignment.degrees = defaultDegrees;
        } else {
          // Clear degrees if number is 0 or negative
          updatedAlignment.degrees = [];
        }
      }

      // Flange: number of readings → empty labels by default (user enters e.g. 12 o'clock to 6 o'clock)
      if (field === 'numberOfFlangeReadings') {
        const count = Math.min(Math.max(Number(value) || 2, 1), 10);
        updatedAlignment.numberOfFlangeReadings = count;
        updatedAlignment.alignmentMode = 'flange';
        const existing = Array.isArray(updatedAlignment.flangeReadings)
          ? updatedAlignment.flangeReadings
          : [];
        updatedAlignment.flangeReadings = Array.from({ length: count }, (_, index) =>
          existing[index] != null ? existing[index] : ''
        );
      }

      return {
        ...prev,
        details: {
          ...prev.details,
          alignment: updatedAlignment
        }
      };
    });
  };

  const updateFlangeReadingLabelField = (index: number, value: string) => {
    setNewSpecification((prev) => {
      const currentAlignment = prev.details?.alignment || {};
      const currentLabels = [...(currentAlignment.flangeReadings || [])];
      currentLabels[index] = value;
      return {
        ...prev,
        details: {
          ...prev.details,
          alignment: {
            ...currentAlignment,
            alignmentMode: 'flange',
            flangeReadings: currentLabels,
          },
        },
      };
    });
  };

  const updateAlignmentDegreeField = (index: number, value: string) => {
    setNewSpecification(prev => {
      const currentAlignment = prev.details?.alignment || {};
      const currentDegrees = currentAlignment.degrees || [];
      const updatedDegrees = [...currentDegrees];

      // Allow empty string to clear the field
      if (value === '') {
        updatedDegrees[index] = '';
      } else {

        // Validate the degree value only if it's not empty
        const numValue = parseInt(value);
        if (!isNaN(numValue) && numValue >= 0 && numValue <= 360) {

          updatedDegrees[index] = numValue;
        } else {
          // If invalid, don't update the value
          return prev;
        }
      }

      return {
        ...prev,
        details: {
          ...prev.details,
          alignment: {
            ...currentAlignment,
            degrees: updatedDegrees
          }
        }
      };
    });
  };

  const updateAcceptanceCriteriaField = (field: string, value: any) => {
    setNewSpecification(prev => ({
      ...prev,
      details: {
        ...prev.details,
        acceptanceCriteria: {
          standard: prev.details?.acceptanceCriteria?.standard || '',
          defectTypes: prev.details?.acceptanceCriteria?.defectTypes || [],
          tolerances: prev.details?.acceptanceCriteria?.tolerances || '',
          repairProcedure: prev.details?.acceptanceCriteria?.repairProcedure || '',
          [field]: value
        }
      }
    }));
  };

  const updatePassField = (index: number, field: 'name' | 'numberOfPasses' | 'requirements', value: string | number) => {
    setPasses(prev => prev.map((pass, i) =>
      i === index ? { ...pass, [field]: value } : pass
    ));
  };

  const addPass = () => {
    const usedPassNames = passes.map((pass) => pass.name);
    setPasses((prev) => [
      ...prev,
      {
        name: nextAvailableWeldingPassName(usedPassNames),
        numberOfPasses: 1,
        requirements: '',
      },
    ]);
  };

  const removePass = (index: number) => {
    setPasses(prev => {
      const newPasses = prev.filter((_, i) => i !== index);
      // Reorder sequences
      return newPasses.map((pass, i) => ({
        ...pass,
      }));
    });
  };

  // NEW PATTERN with FileUpload component:
  const handleFileSelect = async (selectedFiles: FileList | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      const updatedFiles = [...files, ...fileArray];
      setFiles(updatedFiles);

      // For multiple files, we don't need to set a single file in newSpecification
      // The files array will be used directly in the upload process
    }
  };
  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };



  const isConfigurationSetup = (type: string) => {
    return type !== 'FITMENT_READINGS' && type !== 'PASS_CONFIG'
      && type !== 'WELD_PROCESS' && type !== 'ALIGNMENT';
  };

  return (
    <>
      <Dialog open={isOpen}

        onOpenChange={onReset}>
        <DialogContent className="max-w-6xl max-h-[100vh] flex flex-col overflow-y-auto">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Add New Specification</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-2 overflow-x-hidden">
            {/* Fabrication Subtype Dropdown - Only show for fabrication */}

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{isFabricationSetup() ? 'Fabrication Type' : 'Type of Project'} <span className="text-red-500">*</span></Label>
                <PipeSelector
                  value={newSpecification.fabricationSubType || null}
                  onChange={(value) => {
                    setNewSpecification(prev => ({
                      ...prev,
                      fabricationSubType: value,
                      pipeId: '' // reset pipe selection when subtype changes
                    }));
                  }}
                  placeholder="Select fabrication type"
                  triggerWidth="w-full"
                  options={fabricationSubTypes.map((subType) => ({
                    value: subType,
                    label:
                      subType === 'pipeline' ? 'Pipeline' :
                      subType === 'buckleArrestor' ? 'Buckle Arrestor' :
                      subType === 'pipeInPipe' ? 'Pipe in Pipe' :
                      subType === 'bulkhead' ? 'Bulk Head' :
                      subType === 'spools' ? 'Spools' :
                      subType === 'others' ? 'Others' : subType,
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Pipe <span className="text-red-500">*</span>
                  {isFabricationSetup() && newSpecification.fabricationSubType && (
                    <span className="text-sm text-gray-500 ml-2">
                      (Filtered for {newSpecification.fabricationSubType === 'buckleArrestor' ? 'Buckle Arrestor' :
                        newSpecification.fabricationSubType === 'pipeInPipe' ? 'Pipe in Pipe' :
                          newSpecification.fabricationSubType === 'bulkhead' ? 'Bulk Head' :
                            newSpecification.fabricationSubType === 'pipeline' ? 'Pipeline' :
                              newSpecification.fabricationSubType === 'spools' ? 'Spools' :
                                newSpecification.fabricationSubType === 'others' ? 'Others' : newSpecification.fabricationSubType})
                    </span>
                  )}
                </Label>
                <PipeSelector
                  value={newSpecification.pipeId || null}
                  onChange={(value) => setNewSpecification(prev => ({ ...prev, pipeId: value }))}
                  placeholder={
                    isFabricationSetup() && newSpecification.fabricationSubType
                      ? `Select pipe for ${newSpecification.fabricationSubType === 'buckleArrestor' ? 'Buckle Arrestor' :
                        newSpecification.fabricationSubType === 'pipeInPipe' ? 'Pipe in Pipe' :
                          newSpecification.fabricationSubType === 'bulkhead' ? 'Bulk Head' :
                            newSpecification.fabricationSubType === 'pipeline' ? 'Pipeline' :
                              newSpecification.fabricationSubType === 'spools' ? 'Spools' :
                              newSpecification.fabricationSubType === 'others' ? 'Others' : newSpecification.fabricationSubType}`
                      : "Select pipe"
                  }
                  triggerWidth="w-full"
                  options={filteredPipeOptions.map((pipe) => ({
                    value: pipe.pipeId,
                    label: pipe.displayName,
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Specification Type <span className="text-red-500">*</span></Label>
                <PipeSelector
                  value={newSpecification.type || null}
                  onChange={(value) => {
                    setNewSpecification(prev => ({ ...prev, type: value as SpecificationDocument['type'] }));
                  }}
                  placeholder="Select type"
                  triggerWidth="w-full"
                  options={Object.entries(SPEC_TYPE_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </div>


            </div>

            <div className="space-y-2 pt-4">
              <Label>File
                {newSpecification.type !== 'FITMENT_READINGS' && newSpecification.type !== 'PASS_CONFIG'
                  && newSpecification.type !== 'WELD_PROCESS' && newSpecification.type !== 'ALIGNMENT' &&
                  <span className="text-red-500">*</span>}
              </Label>

              <p className="text-sm text-gray-600">
                `Upload the specification {isConfigurationSetup(newSpecification.type) ? 'documents' : 'document'} (PDF,Excel, DOC, etc...)`
              </p>

              <div className="w-full max-w-full">
                <FileUpload
                  onFileSelect={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xlsx,.xls"
                  multiple={isConfigurationSetup(newSpecification.type)}
                  maxFiles={isConfigurationSetup(newSpecification.type) ? 100 : 1}
                  buttonText="Upload Documents"
                  variant="outline"
                  size="lg"
                  onRemoveFile={handleRemoveFile}
                  selectedFiles={files}
                  showFileList={files.length > 0}
                  className="w-full"
                  maxHeight=""
                  showTags={true}
                  optionsTags={tagOptions}
                  selectedTags={selectedTags}
                  showAddButton={true}
                  addButtonText="Add New Tag"
                  onAddButtonClick={() => setIsAddingTag(true)}
                  onTagsChange={handleTagsChange}
                />
              </div>

              {/* Debug: Show current documentFiles count */}
              <div className="text-xs text-gray-500">
                Current files: {files.length} file(s)
              </div>
            </div>

            {/* Dynamic form fields based on specification type */}
            {newSpecification.type === 'WELD_PROCESS' && (
              <div className="space-y-4 pt-4">
                <h4 className="font-medium">Weld Process Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Process Type</Label>
                    <MultiSelect
                      options={processTypeOptions}
                      selected={newSpecification.details?.weldProcess?.processType || []}
                      onChange={(selected: string[]) => updateWeldProcessField('processType', selected)}
                      placeholder="Select process types"
                      isSearchable={true}
                      showAddButton={true}
                      addButtonText="Add New Process Type"
                      onAddButtonClick={() => setIsAddingProcessType(true)}
                    />

                  </div>
                  <div className="space-y-2">
                    <Label>Weld Position</Label>
                    <MultiSelect
                      options={weldPositionOptions}
                      selected={newSpecification.details?.weldProcess?.weldPosition || []}
                      onChange={(selected: string[]) => updateWeldProcessField('weldPosition', selected)}
                      placeholder="Select weld positions"
                      isSearchable={true}
                      showAddButton={true}
                      addButtonText="Add New Weld Position"
                      onAddButtonClick={() => setIsAddingWeldPosition(true)}
                    />

                  </div>
                  <div className="space-y-2">
                    <Label>Joint Type</Label>
                    <MultiSelect
                      options={jointTypeOptions}
                      selected={newSpecification.details?.weldProcess?.jointType || []}
                      onChange={(selected: string[]) => updateWeldProcessField('jointType', selected)}
                      placeholder="Select joint types"
                      isSearchable={true}
                      showAddButton={true}
                      addButtonText="Add New Joint Type"
                      onAddButtonClick={() => setIsAddingJointType(true)}
                      maxHeight="180px"

                    />

                  </div>

                </div>
              </div>
            )}

            {newSpecification.type === 'PASS_CONFIG' && (
              <div className="space-y-4 pt-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">Pass Configuration</h4>
                  <Button
                    type="button"

                    size="sm"
                    onClick={addPass}
                    className="flex items-center gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Pass
                  </Button>
                </div>

                <div className="space-y-4  pr-2">
                  {passes.map((pass, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium">Pass {index + 1}</h5>
                        {passes.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removePass(index)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"

                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Pass Name  <span className="text-red-500">*</span></Label>
                          <PipeSelector
                            value={pass.name || null}
                            onChange={(value: string) => {
                              setPasses(prev => prev.map((pass, i) =>
                                i === index ? { ...pass, name: value } : pass
                              ))
                            }}
                            placeholder="Select pass name"
                            triggerWidth="w-full"
                            options={passOptions.map((option) => ({
                              value: option.value,
                              label: option.label,
                            }))}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Number of Passes  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            value={pass.numberOfPasses}
                            onChange={(e) => updatePassField(index, 'numberOfPasses', parseInt(e.target.value))}
                            min={1}
                            max={20}
                            placeholder="Number of Passes"
                          />
                        </div>

                        {/* <div className="space-y-2">
                        <Label>Requirements</Label>
                        <Input
                          value={pass.requirements}
                          onChange={(e) => updatePassField(index, 'requirements', e.target.value)}
                          placeholder="e.g., Complete penetration, no undercut"
                        />
                      </div> */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {newSpecification.type === 'ALIGNMENT' && (
              <div className="space-y-4 pt-4">
                {selectedPipeIsFlange ? (
                  <>
                    <h4 className="font-medium">Flange Alignment Details</h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Number of Readings <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            value={newSpecification.details?.alignment?.numberOfFlangeReadings || 2}
                            onChange={(e) =>
                              updateAlignmentField(
                                'numberOfFlangeReadings',
                                Math.min(Math.max(parseInt(e.target.value, 10) || 2, 1), 10)
                              )
                            }
                            min={1}
                            max={10}
                            placeholder="Number of Readings (1-10)"
                            className="flex-1"
                          />
                        </div>
                        {(newSpecification.details?.alignment?.numberOfFlangeReadings || 2) > 0 && (
                          <div className="space-y-2 col-span-3">
                            <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
                              {Array.from(
                                {
                                  length:
                                    Number(newSpecification.details?.alignment?.numberOfFlangeReadings) || 2,
                                },
                                (_, index) => (
                                  <div key={index} className="space-y-1">
                                    <Label className="text-xs text-heritage-grey">
                                      Reading #{index + 1}
                                    </Label>
                                    <Input
                                      type="text"
                                      value={
                                        newSpecification.details?.alignment?.flangeReadings?.[index] ?? ''
                                      }
                                      onChange={(e) => updateFlangeReadingLabelField(index, e.target.value)}
                                      placeholder={
                                        DEFAULT_FLANGE_READING_LABELS[index] || `Reading #${index + 1}`
                                      }
                                    />
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>Alignment Location <span className="text-red-500">*</span></Label>
                        <MultiSelect
                          options={alignmentLocationOptions}
                          selected={newSpecification.details?.alignment?.alignmentLocation || []}
                          onChange={(selected: string[]) =>
                            updateAlignmentField('alignmentLocation', selected)
                          }
                          placeholder="Select alignment location"
                          isSearchable={true}
                          showAddButton={true}
                          addButtonText="Add New Alignment Location"
                          onAddButtonClick={() => setIsAddingAlignmentLocation(true)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Note <span className="text-red-500">*</span></Label>
                        <Input
                          value={newSpecification.details?.alignment?.note || ''}
                          onChange={(e) => {
                            setNewSpecification((prev) => ({
                              ...prev,
                              details: {
                                ...prev.details,
                                alignment: {
                                  ...prev.details?.alignment,
                                  alignmentMode: 'flange',
                                  numberOfFlangeReadings:
                                    prev.details?.alignment?.numberOfFlangeReadings || 2,
                                  flangeReadings:
                                    prev.details?.alignment?.flangeReadings ||
                                    buildDefaultFlangeReadingLabels(2),
                                  note: e.target.value,
                                },
                              },
                            }));
                          }}
                          placeholder="Enter Note"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                <h4 className="font-medium">Axial Alignment Details</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Number of Locations  <span className="text-red-500">*</span></Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="number"
                          value={newSpecification.details?.alignment?.numberOfAxialAlignments || '2'}
                          onChange={(e) => updateAlignmentField('numberOfAxialAlignments', parseInt(e.target.value) || 2)}
                          min={2}
                          max={10}
                          defaultValue={2}
                          placeholder="Number of Axial Alignments (2-10)"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    {/* Dynamic Degree Inputs */}
                    {newSpecification.details?.alignment?.numberOfAxialAlignments > 0 && (
                      <div className="space-y-2 col-span-3">

                        <Label>Alignment Degrees</Label>

                        <div className={`grid gap-3 grid-cols-1 md:grid-cols-4`}>
                          {Array.from({ length: newSpecification.details.alignment.numberOfAxialAlignments }, (_, index) => (
                            <div key={index} className="space-y-2">
                              <div className="relative">
                                <Input
                                  type="number"
                                  value={newSpecification.details?.alignment?.degrees?.[index] ?? ''}
                                  onChange={(e) => updateAlignmentDegreeField(index, e.target.value)}
                                  placeholder={`${(index * 90) % 360}°`}
                                  min={0}
                                  max={359}
                                  step={1}
                                  className="pr-8"
                                />
                                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm">
                                  °
                                </span>
                              </div>


                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>




                  <div className="space-y-2">
                    <Label>Alignment Location  <span className="text-red-500">*</span></Label>
                    <MultiSelect
                      options={alignmentLocationOptions}
                      selected={newSpecification.details?.alignment?.alignmentLocation || []}
                      onChange={(selected: string[]) => updateAlignmentField('alignmentLocation', selected)}
                      placeholder="Select alignment location"
                      isSearchable={true}
                      showAddButton={true}
                      addButtonText="Add New Alignment Location"
                      onAddButtonClick={() => setIsAddingAlignmentLocation(true)}
                    />
                  </div>


                  <div className="space-y-2">
                    <Label>Note  <span className="text-red-500">*</span></Label>
                    <Input
                      id="heatNumber"
                      value={newSpecification.details?.alignment?.note || ''}
                      onChange={(e) => setNewSpecification(prev => ({
                        ...prev,
                        details: { ...prev.details, alignment: { ...prev.details?.alignment, note: e.target.value, alignmentMode: 'axial' } }
                      }))}
                      placeholder="Enter Note"
                    />
                  </div>

                </div>
                  </>
                )}
              </div>
            )}

            {newSpecification.type === 'FITMENT_READINGS' && (

              <div className="space-y-4 pt-4">
                <h4 className="font-medium">Fitment Readings Details</h4>

                {newSpecification.fabricationSubType === 'spools' ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label >Internal Hi-Low ({getUnitLabel(unit)})</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Minimum</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newSpecification.details?.fitmentReadings?.internalHiLowMin ?? ''}
                              onChange={(e) => updateFitmentReadingsField('internalHiLowMin', e.target.value === '' ? null : Number(e.target.value))}
                              // className="h-8 text-sm"
                              placeholder="enter minimum of internal hi-low"
                            />
                          </div>
                          <div>
                            <Label>Maximum</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newSpecification.details?.fitmentReadings?.internalHiLowMax ?? ''}
                              onChange={(e) => updateFitmentReadingsField('internalHiLowMax', e.target.value === '' ? null : Number(e.target.value))}
                              //  className="h-8 text-sm"
                              placeholder="enter maximum of internal hi-low"
                            />
                          </div>
                        </div>

                      </div>

                      <div className="space-y-2">
                        <Label >Wall Thickness Top Range ({getUnitLabel(unit)})</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Minimum</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newSpecification.details?.fitmentReadings?.wallThicknessTopRangeMin ?? ''}
                              onChange={(e) => updateFitmentReadingsField('wallThicknessTopRangeMin', e.target.value === '' ? null : Number(e.target.value))}
                              // className="h-8 text-sm"
                              placeholder="enter minimum of wall thickness top range"
                            />
                          </div>
                          <div>
                            <Label>Maximum</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newSpecification.details?.fitmentReadings?.wallThicknessTopRangeMax ?? ''}
                              onChange={(e) => updateFitmentReadingsField('wallThicknessTopRangeMax', e.target.value === '' ? null : Number(e.target.value))}
                              //  className="h-8 text-sm"
                              placeholder="enter maximum of wall thickness top range"
                            />
                          </div>
                        </div>

                      </div>
                      <div className="space-y-2">
                        <Label >Gap Tolerance Range ({getUnitLabel(unit)})</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Minimum</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newSpecification.details?.fitmentReadings?.gapToleranceMin ?? ''}
                              onChange={(e) => updateFitmentReadingsField('gapToleranceMin', e.target.value === '' ? null : Number(e.target.value))}
                              // className="h-8 text-sm"
                              placeholder="enter minimum of gap tolerance range"
                            />
                          </div>
                          <div>
                            <Label>Maximum</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={newSpecification.details?.fitmentReadings?.gapToleranceMax ?? ''}
                              onChange={(e) => updateFitmentReadingsField('gapToleranceMax', e.target.value === '' ? null : Number(e.target.value))}
                              //  className="h-8 text-sm"
                              placeholder="enter maximum of gap tolerance range"
                            />
                          </div>
                        </div>

                      </div>

                    </div>

                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">

                    <div className="space-y-2">
                      <Label >Wall Thickness ({getUnitLabel(unit)})</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label >Minimum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.wallThicknessMin ?? ''}
                            onChange={(e) => updateFitmentReadingsField('wallThicknessMin', e.target.value === '' ? null : Number(e.target.value))}
                            // className="h-8 text-sm"
                            placeholder="enter minimum of wall thickness"
                          />
                        </div>
                        <div>
                          <Label >Maximum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.wallThicknessMax ?? ''}
                            onChange={(e) => updateFitmentReadingsField('wallThicknessMax', e.target.value === '' ? null : Number(e.target.value))}
                            //  className="h-8 text-sm"
                            placeholder="enter maximum of wall thickness"
                          />
                        </div>
                      </div>

                    </div>


                    <div className="space-y-2">
                      <Label >Land ({getUnitLabel(unit)})</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label >Minimum  <span className="text-red-500">*</span>  </Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.landMin ?? ''}
                            onChange={(e) => updateFitmentReadingsField('landMin', e.target.value === '' ? null : Number(e.target.value))}
                            //  className="h-8 text-sm"
                            placeholder="enter minimum of land"
                          />
                        </div>
                        <div>
                          <Label >Maximum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.landMax ?? ''}
                            onChange={(e) => updateFitmentReadingsField('landMax', e.target.value === '' ? null : Number(e.target.value))}
                            //   className="h-8 text-sm"
                            placeholder="enter maximum of land"
                          />
                        </div>

                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label >Internal Hi-Low ({getUnitLabel(unit)})</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label >Minimum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.internalHiLowMin ?? ''}
                            onChange={(e) => updateFitmentReadingsField('internalHiLowMin', e.target.value === '' ? null : Number(e.target.value))}
                            // className="h-8 text-sm"
                            placeholder="enter minimum of internal hi-low"
                          />
                        </div>
                        <div>
                          <Label >Maximum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.internalHiLowMax ?? ''}
                            onChange={(e) => updateFitmentReadingsField('internalHiLowMax', e.target.value === '' ? null : Number(e.target.value))}
                            //  className="h-8 text-sm"
                            placeholder="enter maximum of internal hi-low"
                          />
                        </div>
                      </div>

                    </div>
                    <div className="space-y-2">
                      <Label >External Hi-Low ({getUnitLabel(unit)})</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label >Minimum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.externalHiLowMin ?? ''}
                            onChange={(e) => updateFitmentReadingsField('externalHiLowMin', e.target.value === '' ? null : Number(e.target.value))}
                            // className="h-8 text-sm"
                            placeholder="enter minimum of external hi-low"
                          />
                        </div>
                        <div>
                          <Label >Maximum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.externalHiLowMax ?? ''}
                            onChange={(e) => updateFitmentReadingsField('externalHiLowMax', e.target.value === '' ? null : Number(e.target.value))}
                            //  className="h-8 text-sm"
                            placeholder="enter maximum of external hi-low"
                          />
                        </div>
                      </div>

                    </div>
                    <div className="space-y-2">
                      <Label >Bevel Top Range ({getUnitLabel(unit)})</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label >Minimum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.bevelTopRangeMin ?? ''}
                            onChange={(e) => updateFitmentReadingsField('bevelTopRangeMin', e.target.value === '' ? null : Number(e.target.value))}
                            // className="h-8 text-sm"
                            placeholder="enter minimum of bevel top range"
                          />
                        </div>
                        <div>
                          <Label >Maximum  <span className="text-red-500">*</span></Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={newSpecification.details?.fitmentReadings?.bevelTopRangeMax ?? ''}
                            onChange={(e) => updateFitmentReadingsField('bevelTopRangeMax', e.target.value === '' ? null : Number(e.target.value))}
                            //  className="h-8 text-sm"
                            placeholder="enter maximum of bevel top range"
                          />
                        </div>
                      </div>

                    </div>

                    <div className="space-y-2 pt-6">

                      <Label >No of Readings  <span className="text-red-500">*</span></Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={newSpecification.details?.fitmentReadings?.numberOfReadings ?? ''}
                        onChange={(e) => updateFitmentReadingsField('numberOfReadings', e.target.value === '' ? null : parseInt(e.target.value) || 0)}
                        // className="h-8 text-sm"
                        placeholder="enter number of readings"
                      />

                    </div>

                    {/* <div className="space-y-2">
                  <Label>Gap Tolerance</Label>
                  <Input
                    value={newSpecification.details?.fitmentReadings?.gapTolerance || ''}
                    onChange={(e) => updateFitmentReadingsField('gapTolerance', e.target.value)}
                    placeholder="e.g., 2-4mm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hi-Low Tolerance</Label>
                  <Input
                    value={newSpecification.details?.fitmentReadings?.hiLowTolerance || ''}
                    onChange={(e) => updateFitmentReadingsField('hiLowTolerance', e.target.value)}
                    placeholder="e.g., 1.5mm max"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ovality</Label>
                  <Input
                    value={newSpecification.details?.fitmentReadings?.ovality || ''}
                    onChange={(e) => updateFitmentReadingsField('ovality', e.target.value)}
                    placeholder="e.g., 2mm max"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Straightness</Label>
                  <Input
                    value={newSpecification.details?.fitmentReadings?.straightness || ''}
                    onChange={(e) => updateFitmentReadingsField('straightness', e.target.value)}
                    placeholder="e.g., 3mm per 3m"
                  />
                </div> */}
                  </div>
                )}
              </div>
            )}
            {newSpecification.type === 'WPS' && newSpecification.fabricationSubType === 'spools' && (

              <div className="space-y-4 pt-4">
                <h4 className="font-medium">Preheat Temperature Details</h4>
              
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                  <div className="space-y-2">
                    <Label>
                      Minimum (°C) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      value={newSpecification.details?.preheatTemperature?.minC ?? ''}
                      onChange={(e) =>
                        updatePreheatTemperatureField(
                          'minC',
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                      placeholder="e.g. 100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Maximum (°C) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      value={newSpecification.details?.preheatTemperature?.maxC ?? ''}
                      onChange={(e) =>
                        updatePreheatTemperatureField(
                          'maxC',
                          e.target.value === '' ? null : Number(e.target.value)
                        )
                      }
                      placeholder="e.g. 150"
                    />
                  </div>
                </div>

            
              </div>
            )}

          </div>
          <DialogFooter className="gap-3 mt-6 flex-shrink-0">
            <Button variant="outline" onClick={onReset}>
              Cancel
            </Button>
            <Button
              loading={isLoading}
              loadingText="Submitting..."
              onClick={handleSubmit}
              disabled={files.length === 0 && newSpecification.type !== 'WELD_PROCESS' && newSpecification.type !== 'PASS_CONFIG' && newSpecification.type !== 'FITMENT_READINGS' && newSpecification.type !== 'ALIGNMENT' ||
                !newSpecification.type || !newSpecification.pipeId || isLoading}
            >
              Add Specification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Add New Process Type Dialog */}
      <AddNewItemDialog
        open={isAddingProcessType}
        onOpenChange={setIsAddingProcessType}
        title="Add New Process Type"
        label="Process Type"
        placeholder="Enter process type"
        onAdd={handleAddProcessType}
        buttonText="Add Process Type"
        cancelText="Cancel"
      />
      {/* Add New Weld Position Dialog */}
      <AddNewItemDialog
        open={isAddingWeldPosition}
        onOpenChange={setIsAddingWeldPosition}
        title="Add New Weld Position"
        label="Weld Position"
        placeholder="Enter weld position"
        onAdd={handleAddWeldPosition}
        buttonText="Add Weld Position"
        cancelText="Cancel"
      />
      {/* Add New Joint Type Dialog */}
      <AddNewItemDialog
        open={isAddingJointType}
        onOpenChange={setIsAddingJointType}
        title="Add New Joint Type"
        label="Joint Type"
        placeholder="Enter joint type"
        onAdd={handleAddJointType}
        buttonText="Add Joint Type"
        cancelText="Cancel"
      />
      {/* Add New Alignment Location Dialog */}
      <AddNewItemDialog
        open={isAddingAlignmentLocation}
        onOpenChange={setIsAddingAlignmentLocation}
        title="Add New Alignment Location"
        label="Alignment Location"
        placeholder="Enter alignment location"
        onAdd={handleAddAlignmentLocation}
        buttonText="Add Alignment Location"
        cancelText="Cancel"
      />
      {/* Add New Note Dialog */}
      {/* <AddNewItemDialog
        open={isAddingNote}
        onOpenChange={setIsAddingNote}
        title="Add New Note"
        label="Note"
        placeholder="Enter note"
        onAdd={handleAddNote}
        buttonText="Add Note"
        cancelText="Cancel"
      /> */}

      {/* Add New Tag Dialog */}
      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />


    </>
  );
}; 